import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { AxiosResponse } from 'axios';
import { REACTION_TYPE, ReactionType, ReactionUsers } from 'models/Reactions';
import useCacheFetch from 'hooks/useCacheFetch';
import ReactionPopup from 'components/ReactionPopup';
import ResolvePopup from 'components/ResolvePopup';
import { Button, Loader } from 'ncoded-component-library';
import { User } from 'models/User';
import {
  getIsArrayEmpty,
  getUserFullName,
  isQuestionMarkReaction,
  isThumbsDownReaction,
  isThumbsUpReaction,
} from 'utils';
import LikeIcon from 'icons/Like.icon';
import DislikeIcon from 'icons/Dislike.icon';
import CircledQuestionmarkIcon from 'icons/CircledQuestionmark.icon';

import './Reactions.styles.scss';

type ReactionsProps = {
  className?: string;
  thumbsUp: number;
  thumbsDown: number;
  questionMark: number;
  activeReaction?: ReactionType;
  showWhoReacted?: boolean;
  canReact?: boolean;
  onReact?: (reaction: ReactionType) => void;
  getReactionUsers?: () => Promise<AxiosResponse<ReactionUsers>>;
};

const Reactions: React.FC<ReactionsProps> = (props) => {
  const {
    className,
    thumbsUp,
    thumbsDown,
    questionMark,
    activeReaction,
    showWhoReacted = true,
    canReact = false,
    onReact,
    getReactionUsers,
  } = props;

  const {
    data,
    isLoading,
    fetch: fetchUsers,
  } = useCacheFetch({
    resetCache: [thumbsUp, thumbsDown, questionMark],
    fetchData: getReactionUsers,
  });

  const {
    thumbsUp: thumbsUpUsers,
    thumbsDown: thumbsDownUsers,
    questionMark: questionMarkUsers,
  } = data || {};

  const renderUsers = useCallback((users: Partial<User>[]) => {
    if (getIsArrayEmpty(users)) return;

    return (
      <ul>
        {users.map(({ _id, firstName, lastName }) => {
          const fullName = getUserFullName(firstName, lastName);
          return <li key={_id || fullName}>{fullName}</li>;
        })}
      </ul>
    );
  }, []);

  const classes = classNames('reactions', className);

  const getPopupContent = useCallback(
    (users: Partial<User>[]) => (
      <div className="reactions__popup">
        {isLoading && <Loader size="small" />}
        {renderUsers(users)}
      </div>
    ),
    [isLoading, renderUsers],
  );

  const likePopupContent = useMemo(
    () => getPopupContent(thumbsUpUsers),
    [getPopupContent, thumbsUpUsers],
  );

  const dislikePopupContent = useMemo(
    () => getPopupContent(thumbsDownUsers),
    [getPopupContent, thumbsDownUsers],
  );

  const questionPopupContent = useMemo(
    () => getPopupContent(questionMarkUsers),
    [getPopupContent, questionMarkUsers],
  );

  const handleOnReact = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      reaction: ReactionType,
    ) => {
      e.stopPropagation();
      if (canReact) onReact?.(reaction);
    },
    [canReact, onReact],
  );

  const LikeButton = useMemo(
    () =>
      !!thumbsUp && (
        <Button
          variant="outline"
          className={classNames('like', {
            active: isThumbsUpReaction(activeReaction),
          })}
          icon={LikeIcon}
          onClick={(e) => handleOnReact(e, REACTION_TYPE.THUMBS_UP)}
        >
          {thumbsUp}
        </Button>
      ),
    [activeReaction, handleOnReact, thumbsUp],
  );

  const DislikeButton = useMemo(
    () =>
      !!thumbsDown && (
        <Button
          variant="outline"
          className={classNames('dislike', {
            active: isThumbsDownReaction(activeReaction),
          })}
          icon={DislikeIcon}
          onClick={(e) => handleOnReact(e, REACTION_TYPE.THUMBS_DOWN)}
        >
          {thumbsDown}
        </Button>
      ),
    [activeReaction, handleOnReact, thumbsDown],
  );

  const QuestionButton = useMemo(
    () =>
      !!questionMark && (
        <Button
          variant="outline"
          className={classNames('question-mark', {
            active: isQuestionMarkReaction(activeReaction),
          })}
          icon={CircledQuestionmarkIcon}
          onClick={(e) => handleOnReact(e, REACTION_TYPE.QUESTION_MARK)}
        >
          {questionMark}
        </Button>
      ),
    [activeReaction, handleOnReact, questionMark],
  );

  if (!canReact && !thumbsUp && !thumbsDown && !questionMark) return null;

  return (
    <div className={classes}>
      {showWhoReacted && !!thumbsUp ? (
        <ResolvePopup
          onHover
          popupContent={likePopupContent}
          customOnHover={fetchUsers}
        >
          {LikeButton}
        </ResolvePopup>
      ) : (
        LikeButton
      )}

      {showWhoReacted && !!thumbsDown ? (
        <ResolvePopup
          onHover
          popupContent={dislikePopupContent}
          customOnHover={fetchUsers}
        >
          {DislikeButton}
        </ResolvePopup>
      ) : (
        DislikeButton
      )}

      {showWhoReacted && !!questionMark ? (
        <ResolvePopup
          onHover
          popupContent={questionPopupContent}
          customOnHover={fetchUsers}
        >
          {QuestionButton}
        </ResolvePopup>
      ) : (
        QuestionButton
      )}

      {canReact && onReact && (
        <ReactionPopup activeReaction={activeReaction} onReact={onReact} />
      )}
    </div>
  );
};

export default Reactions;
